import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Home, Sun, Moon, MessageCircle, Menu, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import logoIcon from '../assets/images/logo-icon.png'
import RequestPanel from '../components/api-explorer/RequestPanel.tsx'
import ResponsePanel from '../components/api-explorer/ResponsePanel.tsx'
import DocumentationPanel from '../components/api-explorer/DocumentationPanel.tsx'
import RequestHistoryFAB from '../components/api-explorer/RequestHistoryFAB.tsx'
import ResizeHandle from '../components/ResizeHandle'
import ErrorBoundary from '../components/ErrorBoundary'
import { useUIStore } from '../stores/apiStore'

const APIExplorerPage: React.FC = () => {
  const { leftPanelWidth, rightPanelWidth, adjustPanelWidth } = useUIStore()
  const centerPanelWidth = 100 - leftPanelWidth - rightPanelWidth
  const navigate = useNavigate()
  const [isDarkMode, setIsDarkMode] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Check if dark mode is enabled
    const isDark = document.documentElement.classList.contains('dark')
    setIsDarkMode(isDark)
  }, [])

  const toggleDarkMode = () => {
    const newMode = !isDarkMode
    setIsDarkMode(newMode)
    if (newMode) {
      document.documentElement.classList.add('dark')
      localStorage.setItem('theme', 'dark')
    } else {
      document.documentElement.classList.remove('dark')
      localStorage.setItem('theme', 'light')
    }
  }

  const handleHome = () => {
    navigate('/')
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <motion.div 
        className="sticky top-0 z-40 bg-white/90 dark:bg-gray-900/90 header-blur border-b border-gray-200/50 dark:border-gray-700/50 header-shadow"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-full mx-auto px-3 sm:px-4 lg:px-6 xl:px-8">
          <div className="flex items-center justify-between h-16 sm:h-18 md:h-20">
            {/* Left Section - Logo and Title */}
            <div className="flex items-center min-w-0 flex-1 lg:flex-none">
              <div className="flex items-center space-x-2 sm:space-x-3">
                {/* MomentScience Logo */}
                <div className="relative flex-shrink-0">
                  <img 
                    src={logoIcon} 
                    alt="MomentScience" 
                    className="w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 object-contain drop-shadow-sm"
                  />
                </div>
                {/* Title - Responsive text sizing */}
                <div className="flex flex-col -space-y-0.5 min-w-0 header-text-responsive">
                  <h1 className="text-sm sm:text-base md:text-lg font-bold text-[#FE4215] dark:text-[#FF6B35] tracking-tight truncate">
                    MomentScience Labs
                  </h1>
                  <h2 className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 tracking-wide truncate">
                    API Explorer
                  </h2>
                </div>
              </div>
            </div>
            
            {/* Right Section - Controls */}
            <div className="flex items-center space-x-1 sm:space-x-2 md:space-x-3 flex-shrink-0">
              {/* Version Badge - Progressive visibility */}
              <div className="hidden xl:flex px-2.5 py-1 bg-gradient-to-r from-[#FE4215]/10 to-[#FF6B35]/10 border border-[#FE4215]/20 text-[#FE4215] dark:text-[#FF6B35] rounded-full text-xs font-semibold tracking-wide backdrop-blur-sm">
                v1.0.0
              </div>

              {/* Theme Toggle */}
              <button
                onClick={toggleDarkMode}
                className="p-1.5 sm:p-2 md:p-2.5 rounded-full bg-gray-100/80 dark:bg-gray-800/80 hover:bg-gray-200/80 dark:hover:bg-gray-700/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 transition-all duration-200 hover:shadow-md group flex-shrink-0"
                title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDarkMode ? (
                  <Sun className="w-4 h-4 text-amber-500 group-hover:text-amber-400 transition-colors" />
                ) : (
                  <Moon className="w-4 h-4 text-gray-600 group-hover:text-[#FE4215] transition-colors" />
                )}
              </button>

              {/* Desktop Buttons - Progressive visibility */}
              <div className="hidden md:flex items-center space-x-2">
                {/* Contact Us Button */}
                <button
                  onClick={() => window.open('https://momentscience.com/contact-us', '_blank')}
                  className="flex items-center space-x-1.5 px-3 md:px-4 py-2 md:py-2.5 bg-gray-600/90 hover:bg-gray-700/90 text-white rounded-full transition-all duration-200 hover:shadow-lg backdrop-blur-sm border border-gray-500/30 group flex-shrink-0"
                  title="Contact us for support"
                >
                  <MessageCircle className="w-3.5 h-3.5 md:w-4 md:h-4 group-hover:scale-110 transition-transform flex-shrink-0" />
                  <span className="text-xs md:text-sm font-medium whitespace-nowrap hidden lg:inline">Contact Us Now</span>
                </button>

                {/* Home Button */}
                <button
                  onClick={handleHome}
                  className="flex items-center space-x-1.5 px-3 md:px-4 py-2 md:py-2.5 bg-gradient-to-r from-[#FE4215] to-[#FF6B35] hover:from-[#E5350A] hover:to-[#FF6B35] text-white rounded-full transition-all duration-200 hover:shadow-lg hover:shadow-[#FE4215]/25 backdrop-blur-sm group flex-shrink-0"
                  title="Go to home"
                >
                  <Home className="w-3.5 h-3.5 md:w-4 md:h-4 group-hover:scale-110 transition-transform flex-shrink-0" />
                  <span className="text-xs md:text-sm font-medium whitespace-nowrap hidden lg:inline">Home</span>
                </button>
              </div>

              {/* Mobile Menu Button - Show on smaller screens */}
              <div className="block md:hidden">
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="p-1.5 sm:p-2 rounded-full bg-gray-100/80 dark:bg-gray-800/80 hover:bg-gray-200/80 dark:hover:bg-gray-700/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 transition-all duration-200 hover:shadow-md group flex-shrink-0"
                  title="Menu"
                >
                  {isMobileMenuOpen ? (
                    <X className="w-4 h-4 text-gray-600 dark:text-gray-400 group-hover:text-[#FE4215] transition-colors" />
                  ) : (
                    <Menu className="w-4 h-4 text-gray-600 dark:text-gray-400 group-hover:text-[#FE4215] transition-colors" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Menu - Improved design and animations */}
          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0, y: -10 }}
                animate={{ opacity: 1, height: 'auto', y: 0 }}
                exit={{ opacity: 0, height: 0, y: -10 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="md:hidden border-t border-gray-200/50 dark:border-gray-700/50 overflow-hidden"
              >
                <div className="py-4 space-y-3">
                  {/* Version Badge */}
                  <div className="flex justify-center px-4">
                    <div className="inline-flex px-3 py-1.5 bg-gradient-to-r from-[#FE4215]/10 to-[#FF6B35]/10 border border-[#FE4215]/20 text-[#FE4215] dark:text-[#FF6B35] rounded-full text-xs font-semibold tracking-wide backdrop-blur-sm">
                      v1.0.0
                    </div>
                  </div>
                  
                  {/* Mobile Buttons */}
                  <div className="px-4 space-y-2">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        window.open('https://momentscience.com/contact-us', '_blank')
                        setIsMobileMenuOpen(false)
                      }}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gray-600/90 hover:bg-gray-700/90 text-white rounded-xl transition-all duration-200 hover:shadow-lg backdrop-blur-sm border border-gray-500/30 group"
                    >
                      <MessageCircle className="w-4 h-4 group-hover:scale-110 transition-transform" />
                      <span className="text-sm font-medium">Contact Us Now</span>
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        handleHome()
                        setIsMobileMenuOpen(false)
                      }}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-[#FE4215] to-[#FF6B35] hover:from-[#E5350A] hover:to-[#FF6B35] text-white rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-[#FE4215]/25 backdrop-blur-sm group"
                    >
                      <Home className="w-4 h-4 group-hover:scale-110 transition-transform" />
                      <span className="text-sm font-medium">Go to Home</span>
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Main Content */}
      <div 
        ref={containerRef} 
        className="flex relative overflow-hidden" 
        style={{ 
          height: `calc(100vh - ${isMobileMenuOpen ? '160px' : '64px'})`,
          minHeight: '300px'
        }}
      >
        {/* Left Panel - Request Configuration */}
        <motion.div 
          className="bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-hidden relative"
          style={{ width: `${leftPanelWidth}%` }}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <ErrorBoundary>
            <RequestPanel />
          </ErrorBoundary>
        </motion.div>

        {/* Left Resize Handle */}
        <ResizeHandle
          direction="horizontal"
          onResize={(delta) => {
            if (containerRef.current) {
              adjustPanelWidth('left', delta, containerRef.current.offsetWidth)
            }
          }}
          className="bg-gray-200 dark:bg-gray-700 hover:bg-[#FE4215] transition-colors z-10"
        />

        {/* Center Panel - Response Viewer */}
        <motion.div 
          className="bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-hidden relative"
          style={{ width: `${centerPanelWidth}%` }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <ErrorBoundary>
            <ResponsePanel />
          </ErrorBoundary>
        </motion.div>

        {/* Right Resize Handle */}
        <ResizeHandle
          direction="horizontal"
          onResize={(delta) => {
            if (containerRef.current) {
              adjustPanelWidth('right', delta, containerRef.current.offsetWidth)
            }
          }}
          className="bg-gray-200 dark:bg-gray-700 hover:bg-[#FE4215] transition-colors z-10"
        />

        {/* Right Panel - Documentation & Tools */}
        <motion.div 
          className="bg-white dark:bg-gray-800 overflow-hidden relative"
          style={{ width: `${rightPanelWidth}%` }}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <ErrorBoundary>
            <DocumentationPanel />
          </ErrorBoundary>
        </motion.div>
      </div>

      {/* Floating Action Button for Request History */}
      <RequestHistoryFAB />
    </div>
  )
}

export default APIExplorerPage
